/**
 * What the plan trains, as the inspector draws it: up to five ranked rows, each with its own track,
 * one line for everything else, and a body with the worked muscles lit.
 *
 * ── A PORT of the mobile app's `resolveSplit`, plus the remainder ────────────────────────────
 * `move-with-me/src/features/plans/view/model/muscleSplit.ts`: sort by share DESCENDING before the
 * top-five cut, index-stable on ties, drop a group this build cannot name. The sort is the whole
 * point of that module and the reason it is copied rather than reinvented — the backend emits
 * slices in ANATOMICAL card order, not by magnitude, so an unsorted `slice(0, 5)` silently drops
 * the largest groups of a leg-dominant plan.
 *
 * What is added here is owner decision **D5**: the share the five ranked groups do not account for,
 * so what the pane prints totals 100%. Mobile has no such figure — its bar is `flex: percent`, so the
 * five it keeps fill the width whatever they sum to. On a desktop screen with the percentages
 * printed beside the names, five numbers summing to 84 is a question the trainer has to answer for
 * themselves.
 *
 * **The remainder is a SENTENCE, not a ranked row** (owner's polish, 4 November 2026; it was a sixth
 * `Inne` row). A remainder is not a muscle group: as a row it competed for the leader's place — on a
 * full-body plan it was regularly the longest bar, so the readout said the plan was mostly built on
 * "other" — and it set the scale every real group's track was measured against. The ranked rows are
 * now the five real groups alone, measured against the first of them, and the rest is one line under
 * them: `Pozostałe partie razem 16%`.
 *
 * The remainder still carries three different absences at once, deliberately: the sixth and later
 * groups, every slice whose group name this build cannot resolve, and the contract's own `Other`
 * group. All three are "work this plan prescribes that the rows above do not name". `Other` is
 * excluded from the five ranked groups rather than allowed to compete for a place among them,
 * because a group called "other" ranked as a muscle group says nothing about the plan — with one
 * exception: a split where `Other` is the ONLY nameable group ranks it alone (`otherOnlyOf`).
 *
 * ── The BAR's colours are the fourteen `--mg-*`, theme-independent, and NOT clustered ────────
 * Owner decision D5 again: the backend emits fourteen `MuscleGroup` values and the trainer's own
 * exercise library makes all fourteen distinctions, so folding them into the canon's six would
 * throw away a difference the data has. `muscleGroupColour` is the fourteen-group scale;
 * `muscleColour` beside it is the plan BUILDER's six display clusters and is a different thing.
 *
 * ── The silhouette is MONOCHROME, on three steps (owner, 16 September 2026) ──────────────────
 * The figure used to paint each group its own `--mg-*` hue at an intensity scaled to its share:
 * two variables at once, and a drawing that read as a palette rather than as a body. The bar keeps
 * the hues, because the ranked rows genuinely need telling apart; the figure
 * drops them, because a muscle's POSITION already says which muscle it is. `--silhouette-*` in
 * `tokens/colors.css` carries the reasoning and the four values.
 *
 * Which step a group takes is still relative to the plan's own biggest group, for the reason the
 * ramp was relative: a plan's shares depend on how many groups it touches, so a fixed cut-off would
 * draw a full-body plan blank and a push day bright. Groups the plan does not touch are absent from
 * the map and take `BodySilhouette`'s `baseFill`, which is the fourth step.
 *
 * **Every recognised group above the floor lights the body, not only the five the ranking names.**
 * The ranking has room for five names; the figure has room for every muscle. The floor is
 * `MIN_LIT_PERCENT` and it is the owner's (16 September 2026): under 5% a group is a stray accessory
 * rather than something the plan trains, and a mark on the body would say otherwise.
 *
 * `Other` and `FullBody` light nothing — `REGIONS` has no anatomy for either, which is correct:
 * neither names a place on the body.
 *
 * No React, no DOM, no query hook, no i18next. `t` arrives as a PARAMETER and resolves KEYS only.
 */

import {
  muscleGroupFromApi,
  muscleGroupLabelKey,
  muscleGroupToApi,
  type MuscleGroup,
} from '~/entities/exercise-library';
import type { BodySlug } from '~/shared/ui/bodyModel/bodyArtwork';
import { REGIONS } from '~/shared/ui/bodyModel/regions';
import { muscleGroupColour } from '~/shared/ui/muscleColour';
import type { TrainWithMeModulesPlansDtosResponsesMuscleSplitSliceResponse as MuscleSplitSlice } from '~/shared/api/generated/model';

import type { TranslateKey } from './planLibraryRow';

/** How many groups the ranking names before the rest becomes the remainder. Mobile's `MAX_SPLIT_SLICES`. */
const MAX_NAMED_GROUPS = 5;

/** One row of the ranking: the group's name, its own track, and its share. */
export interface MuscleSegment {
  /** The trainer's word for the group. Already resolved — the label is a key, not a count. */
  readonly label: string;
  /** Whole percent. The row prints it and sizes its track against the plan's leader by it. */
  readonly percent: number;
  /** `var(--mg-…)`, unmixed. Theme-independent: these are KANON data, not a palette to tune. */
  readonly colour: string;
}

export interface MuscleSplitView {
  /** Up to five named groups, biggest first. The first is the plan's leader. */
  readonly segments: readonly MuscleSegment[];
  /**
   * Whole percent the ranked groups do not account for: `100 −` their sum, and `0` when they total
   * 100 or more. The pane prints it as one line under the ranking, and only when it is positive.
   */
  readonly remainderPercent: number;
  /** What to paint each worked muscle. Absent slugs take the silhouette's own `baseFill`. */
  readonly fillBySlug: Readonly<Partial<Record<BodySlug, string>>>;
}

/**
 * A plan whose exercises carry no muscles the catalogue names — a brand-new plan, or one built
 * entirely from custom exercises. The section keeps its reserved box and says so in one sentence.
 */
export const EMPTY_MUSCLE_SPLIT: MuscleSplitView = { segments: [], remainderPercent: 0, fillBySlug: {} };

/** One recognised slice, with the position it arrived in — the tie-break the mobile port relies on. */
interface ResolvedSlice {
  readonly group: MuscleGroup;
  readonly percent: number;
  readonly index: number;
}

/**
 * Resolve the wire's slices to groups this build can name, dropping the rest.
 *
 * `Math.round` before anything else, as mobile does: the percentages are printed as whole numbers,
 * and rounding at the end would make the remainder disagree with the sum of what is drawn.
 */
function resolvedSlices(split: readonly MuscleSplitSlice[]): ResolvedSlice[] {
  const out: ResolvedSlice[] = [];
  split.forEach((slice, index) => {
    const group = muscleGroupFromApi(slice.group);
    if (group === null) return;
    out.push({ group, percent: Math.round(slice.percent ?? 0), index });
  });
  return out;
}

/** The five biggest NAMEABLE groups, biggest first, index-stable on ties. */
function rankedOf(resolved: readonly ResolvedSlice[], t: TranslateKey): MuscleSegment[] {
  return resolved
    .filter((slice) => slice.group !== 'other')
    .sort((a, b) => b.percent - a.percent || a.index - b.index)
    .slice(0, MAX_NAMED_GROUPS)
    .map((slice) => ({
      label: t(muscleGroupLabelKey(slice.group)),
      percent: slice.percent,
      colour: muscleGroupColour(slice.group),
    }));
}

/**
 * What the ranked rows leave out: `100 − the five shown`. A stronger rule than "the sixth and later
 * groups", it covers the unrecognised slices dropped above and the contract's `Other` with the same
 * arithmetic. Never negative — a wire whose slices overshoot 100 through rounding has nothing left.
 */
function remainderOf(ranked: readonly MuscleSegment[]): number {
  return Math.max(0, 100 - ranked.reduce((sum, segment) => sum + segment.percent, 0));
}

/**
 * Where a group sits on the figure's three worked steps, as a share of the plan's own largest.
 *
 * Owner decision, 16 September 2026: the figure carries THREE fixed steps rather than a continuous
 * ramp. A ramp draws the difference between 25% and 24% that nobody can see and hides the
 * difference between "this plan is built on it" and "this plan touches it", which is the only
 * reading the drawing is for. Three steps make that reading, and they make it the same way on every
 * plan.
 *
 * The thresholds are relative because the shares are: a full-body plan's largest group is around
 * 24% and a push day's is 40%, so a fixed cut-off would draw one plan bright and the other nearly
 * blank. Against the plan's own leader, a group at two thirds of it reads as a main lift on both.
 */
const STEP_STRONG = 0.7;
const STEP_MID = 0.4;

/**
 * The share below which a group is not on the figure at all. Owner, 16 September 2026.
 *
 * ABSOLUTE percent, not a fraction of the leader, and that is the point of it: the three steps are
 * relative so that two plans compare, and this one is the floor under all of them. A group at 3% is
 * a stray accessory somewhere in the plan, and lighting it puts a mark on the body that says "this
 * is trained" about work nobody prescribed on purpose. Under a relative floor a push day's 3% and a
 * full-body plan's 3% would be treated differently, which is the opposite of what a floor is for.
 *
 * Five rather than one: the shares are rounded to whole percent upstream, so the smallest thing the
 * figure can be asked to draw is 1%, and a 1% mark is indistinguishable from a 4% one at this size.
 * The boundary is inclusive-at-five — a group AT 5% is still drawn, a group under it is not.
 */
const MIN_LIT_PERCENT = 5;

function stepToken(share: number): string {
  if (share >= STEP_STRONG) return 'var(--silhouette-strong)';
  if (share >= STEP_MID) return 'var(--silhouette-mid)';
  return 'var(--silhouette-weak)';
}

/**
 * Which muscles to light, and on which of the three steps.
 *
 * A TOKEN per step, not a mixed colour: the four steps are one ink at four alphas and the theme
 * flips the ink, so the decision here is which step a group has earned and nothing about what that
 * step looks like. The figure's own `baseFill` is the fourth step and the caller passes it.
 *
 * `--mg-*` is deliberately absent. It keeps the BARS, at full strength — see the header.
 */
function fillsOf(resolved: readonly ResolvedSlice[]): Partial<Record<BodySlug, string>> {
  const largest = resolved.reduce((max, slice) => Math.max(max, slice.percent), 0);
  if (largest <= 0) return {};

  const fills: Partial<Record<BodySlug, string>> = {};
  for (const slice of resolved) {
    const contract = muscleGroupToApi(slice.group);
    // `Object.hasOwn` before indexing: a bare index walks the prototype chain, and `REGIONS` is
    // deliberately missing `Other` and `FullBody` — neither names a place on the body.
    if (!Object.hasOwn(REGIONS, contract)) continue;
    // Under the floor the group is not on the figure at all — see `MIN_LIT_PERCENT`. It covers the
    // zero case too: a slice the wire sent as nothing is not work, however the rounding got there.
    if (slice.percent < MIN_LIT_PERCENT) continue;
    const token = stepToken(slice.percent / largest);
    for (const region of REGIONS[contract] ?? []) {
      fills[region.muscle] = token;
    }
  }
  return fills;
}

/**
 * A split whose only nameable group is the contract's `Other` (coordinator's decision of
 * 4 November 2026, register D42). There is no real group for `Inne` to out-rank, and the plan's
 * exercises DO carry muscle groups, so the empty view's sentence would be false: the ranking is the
 * single `Inne` row at 100%, exactly as before the polish, and no remainder line is drawn under it.
 * `Other` names no place on the body, so the figure stays unlit.
 */
function otherOnlyOf(t: TranslateKey): MuscleSplitView {
  return {
    segments: [
      {
        label: t(muscleGroupLabelKey('other')),
        percent: 100,
        colour: muscleGroupColour('other'),
      },
    ],
    remainderPercent: 0,
    fillBySlug: {},
  };
}

/** Project the plan's muscle split. An absent or unnameable split yields the empty view. */
export function toMuscleSplitView(
  split: readonly MuscleSplitSlice[] | null | undefined,
  t: TranslateKey,
): MuscleSplitView {
  const resolved = resolvedSlices(split ?? []);
  if (resolved.length === 0) return EMPTY_MUSCLE_SPLIT;
  const segments = rankedOf(resolved, t);
  if (segments.length === 0) return otherOnlyOf(t);
  return { segments, remainderPercent: remainderOf(segments), fillBySlug: fillsOf(resolved) };
}
